// CollectionsDashboardPage — operations dashboard for the Collections module
// (`/dashboard`). Reached from the post-login HomePage by selecting the
// Collections module card.
//
// Modernised layout but covers every legacy data point from UI_AUDIT §2.1:
//   - Row 1: 4 primary KPI cards (Total / Collected / Paid / Cancelled today)
//   - Row 2: 2 inventory placeholders (Pending Depollution / Ready to Scrap —
//            no server endpoint yet, rendered as "—" + hint).
//   - Row 3: Today summary (status breakdown) | Queue summary (pending + cancellations)
//   - Row 4: Recent activity feed
//
// All data refreshes every 60s via React Query refetchInterval (staleTime 55s).
// "Last updated HH:MM:SS" timestamp tracks the freshest sub-query.

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  FilePlus2,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Button } from '../../../components/ui/Button/Button';
import { StatusBadge } from '../../../components/ui/StatusBadge/StatusBadge';
import type { BadgeVariant } from '../../../components/ui/Badge/Badge';
import { SubscriptionGate } from '../../../components/SubscriptionGate/SubscriptionGate';
import { useToast } from '../../../hooks/useToast';
import { useAuthStore } from '../../../store/authStore';
import { StatsStrip } from '../../../components/ui/StatsStrip/StatsStrip';
import { ActivityFeed } from './components/ActivityFeed';
import { useDashboard } from './hooks/useDashboard';
import { useCollectionsOutletContext } from '../CollectionsLayout';
import type { BookingStatus, UserSummary } from '../../../types/api';
import styles from './CollectionsDashboardPage.module.css';

// Roles permitted to use the Skip quick actions (mirrors the server
// staff/manager/admin gate; operators are kiosk-only).
const SKIP_QUICK_ACTION_ROLES: ReadonlyArray<UserSummary['role']> = [
  'staff',
  'manager',
  'admin',
];

const BOOKING_STATUS_VARIANTS: Record<BookingStatus, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancellation_requested: 'danger',
  cancelled: 'muted',
};

function formatTime(timestamp: number): string {
  if (!timestamp) return '—';
  const dt = new Date(timestamp);
  return dt.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function CollectionsDashboardPage(): JSX.Element {
  const dashboard = useDashboard();
  const toast = useToast();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const { openSkipEnquiry, openSkipUpdateJob } = useCollectionsOutletContext();
  const canUseSkip = role !== undefined && SKIP_QUICK_ACTION_ROLES.includes(role);

  useEffect(() => {
    if (dashboard.isError) {
      toast.error('Some dashboard data could not be loaded. Refresh to retry.');
    }
  }, [dashboard.isError, toast]);

  return (
    <div className={styles.shell}>
      <PageHeader
        title="Dashboard"
        subtitle="Real-time overview of today's operations"
        actions={
          <div className={styles.headerActions}>
            <span
              className={styles.lastUpdated}
              aria-live="polite"
              aria-label={`Last updated at ${formatTime(dashboard.lastUpdatedAt)}`}
            >
              Last updated {formatTime(dashboard.lastUpdatedAt)}
            </span>
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => navigate('/collections/bookings/create')}
            >
              New Booking
            </Button>
          </div>
        }
      />

      {dashboard.isError ? (
        <div className={styles.errorBanner} role="alert">
          One or more metrics failed to load. The numbers shown may be stale.
        </div>
      ) : null}

      {/* Rows 1 + 2 — KPI and inventory stats, dark-banded. */}
      <div className={styles.statsSection}>
        <StatsStrip
          variant="dark"
          ariaLabel="Today's collections summary"
          tiles={[
            { key: 'total', label: 'Total Bookings', value: dashboard.isLoading ? '…' : dashboard.kpis.total.count },
            { key: 'collected', label: 'Collected', value: dashboard.isLoading ? '…' : dashboard.kpis.collected.count },
            { key: 'paid', label: 'Paid', value: dashboard.isLoading ? '…' : dashboard.kpis.paid.count },
            { key: 'cancelled', label: 'Cancelled', value: dashboard.isLoading ? '…' : dashboard.kpis.cancelled.count },
          ]}
        />
        {/* TODO(api): Pending Depollution + Ready to Scrap counts.
            Required endpoint: GET /api/v1/scrap/inventory or similar. */}
        <StatsStrip
          variant="dark"
          ariaLabel="Inventory summary"
          tiles={[
            { key: 'depollution', label: 'Pending Depollution', value: dashboard.inventory.pendingDepollution ?? '—' },
            { key: 'scrap', label: 'Ready to Scrap', value: dashboard.inventory.readyToScrap ?? '—' },
          ]}
        />
      </div>

      {/* Row 2.5 — Skip quick actions. Role-gated (staff/manager/admin)
          and behind the Skip SubscriptionGate — a compact window into the
          Skip module without leaving Collections. */}
      {canUseSkip ? (
        <SubscriptionGate feature="skip">
          <Card
            title="Skip"
            actions={
              <Button
                variant="ghost"
                size="sm"
                icon={Container}
                onClick={() => navigate('/skip/dashboard')}
              >
                Skip Management
              </Button>
            }
          >
            <div className={styles.skipQuickActions}>
              <Button
                variant="secondary"
                icon={FilePlus2}
                onClick={openSkipEnquiry}
              >
                New Enquiry
              </Button>
              <Button
                variant="secondary"
                icon={RefreshCw}
                onClick={openSkipUpdateJob}
              >
                Update Active Job
              </Button>
            </div>
          </Card>
        </SubscriptionGate>
      ) : null}

      {/* Row 3 — Today's bookings summary | Queue summary. */}
      <div className={styles.summaryGrid}>
        <Card title="Today's Bookings">
          {dashboard.statusBreakdown.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
              {dashboard.isLoading ? 'Loading…' : 'No bookings scheduled for today.'}
            </p>
          ) : (
            <ul className={styles.statusList}>
              {dashboard.statusBreakdown.map((entry) => (
                <li key={entry.status} className={styles.statusRow}>
                  <StatusBadge status={entry.status} statusMap={BOOKING_STATUS_VARIANTS} />
                  <span className={styles.statusCount}>{entry.count}</span>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.summaryFooter}>
            <Link to="/collections/diary/today" style={{ textDecoration: 'none' }}>
              <Button variant="ghost" size="sm">
                View today's diary
              </Button>
            </Link>
          </div>
        </Card>

        <Card title="Queue">
          <ul className={styles.statusList}>
            <li className={styles.queueRow}>
              <span className={styles.queueLabel}>Pending</span>
              <span className={styles.queueValue}>{dashboard.pendingOpen}</span>
            </li>
            <li className={styles.queueRow}>
              <span className={styles.queueLabel}>Final Notice</span>
              <span className={`${styles.queueValue} ${styles.queueValueAccent}`}>
                {dashboard.pendingFinalNotice}
              </span>
            </li>
            <li className={styles.queueRow}>
              <span className={styles.queueLabel}>Cancellation Requests</span>
              <span className={`${styles.queueValue} ${styles.queueValueAccent}`}>
                {dashboard.cancellationRequests}
              </span>
            </li>
          </ul>
          <div className={styles.summaryFooter}>
            <Link to="/collections/pending" style={{ textDecoration: 'none' }}>
              <Button variant="ghost" size="sm">
                View pending
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Row 4 — Activity feed. */}
      <Card title="Recent Activity">
        <ActivityFeed entries={dashboard.activity} loading={dashboard.isLoading} />
        <div className={styles.feedFooter}>
          <Link to="/activity-log" style={{ textDecoration: 'none' }}>
            <Button variant="ghost" size="sm">
              View full log
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
